import * as React from 'react';
import type { IconGlyph } from '@sunwebgroup/ui-components';
import {
  type CheckoutCarRentalServiceOption,
  type CheckoutCarRentalServiceOptionFeature,
  ServiceType
} from '../../../domain/types';
import { tk, Typography, ListItem, Icon, SpaceBetween, BulletList } from '@sunwebgroup/ui-components';
import { useIntl } from '@/intl';
import { styled } from 'styled-components';
import { useCarRentalServiceStore } from '@/scopes/checkout/components/services/CarRentalService/store';
import { useService } from '@/scopes/checkout/domain/hooks';
import { sub } from 'date-fns';
import { getIfCarIsSelected } from '@/scopes/checkout/components/services/CarRentalService/EditFormContainerView';

export function CarContentView({
  value,
  driverId,
  ...rest
}: CheckoutCarRentalServiceOption & { priceSymbol?: string }) {
  const { service } = useService(ServiceType.CarRental);

  const { configurations } = service || {};
  const driver = driverId ? service?.participants[driverId] : null;

  const defaultDriverBirthdate = sub(new Date(), { years: configurations?.rentalConditionsMinAgeDriver, days: 1 });
  const { startEditingCar, endEditingCarsExcept } = useCarRentalServiceStore();

  if (!service) return;
  const selected = getIfCarIsSelected(value, service);

  const handleCardClick = () => {
    const selectedCars = service?.carLists?.map(({ selectedValue }) => selectedValue) ?? [];
    endEditingCarsExcept(selectedCars);
    startEditingCar(value, {
      driverId: null,
      driverBirthDate: defaultDriverBirthdate,
      driverHasCreditCard: false,
      bookedEquipments: [],
      fieldsWithError: {},
      isEditing: false,
    });
  };

  return (
    <CarCard {...rest} driver={driver} selected={selected} onClick={handleCardClick} />
  );
}
interface CarCardProps {
  label: string;
  price: number;
  features: CheckoutCarRentalServiceOptionFeature[];
  extraFeatures?: CheckoutCarRentalServiceOptionFeature[];
  equipments: {
    id: string;
    text: string;
  }[];
  selectedEquipments: string[];
  location: {
    text: string;
    icon: IconGlyph | null;
  };
  image: string;
  carClass: string;
  availableOnRequest: boolean;
  priceSymbol?: string;
  onClick?: () => void;
  driver?: {
    name: string;
    birthdate: string | null;
  } | null;
  selected?: boolean;
}

export function CarCard({
  image,
  label,
  carClass,
  features,
  extraFeatures = [],
  equipments,
  selectedEquipments,
  driver,
  selected,
  price,
  location,
  availableOnRequest,
  priceSymbol = '',
  onClick,
}: CarCardProps) {

  const intl = useIntl();
  const selectedEquipmentsList = equipments.filter(({ id }) => selectedEquipments.includes(id));

  return (
    <CarContent onClick={onClick}>
      {image && <CarImage src={image} alt={label} width={300} height={300} />}
      <CarInformation direction="y" spacing={tk('spacing_2xs')}>
        <CarClass $variant="body-xs">{carClass}</CarClass>
        {availableOnRequest && (
          <AvailableOnRequestBadge $state="info">
            {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_available_on_request_label' })}
          </AvailableOnRequestBadge>
        )}
        <ListItem.Label>{label}</ListItem.Label>
        <CarFeatures $size="small" $variant="multiline">
          {[...features, ...extraFeatures].map(({ glyph, text }) => (
            <CarRentalUsp key={glyph} $icon={glyph}>
              {text}
            </CarRentalUsp>
          ))}
        </CarFeatures>
        {driver && (
          <Typography $variant="label">
            {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_selected_driver' })}
            <Typography $variant="body-sm">
              {driver.name} ({driver.birthdate})
            </Typography>
          </Typography>
        )}
        { selected && (
          <SpaceBetween direction='x' spacing={tk('spacing_xs')}>
            <Icon glyph="IconInformationCircle" size="xs"/>
            <Typography $variant="body-xs">
              {intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_edit_form_advise'})}
            </Typography>
          </SpaceBetween>
        )}
        { (selected && selectedEquipmentsList.length) ? (
            <CarSelectedEquipments equipments={selectedEquipmentsList} />
          ) : null
        }
        { (!selected && equipments.length) ?  (
          <CarAvailableEquipments equipments={equipments} /> ) : null
        }

      </CarInformation>
      <CarPriceView direction="y" alignItems="end">
        <PriceDetail as="span" $variant="body-xs">
          {location.text}
          {location.icon && <Icon glyph={location.icon} />}
        </PriceDetail>
        <Price as="span" $variant="heading-md">
          {priceSymbol} {intl.formatCurrency(price)}
        </Price>
      </CarPriceView>
    </CarContent>
  );
}

function CarSelectedEquipments ({equipments}: {equipments: {id: string; text: string}[]}) {
  const intl = useIntl();
  if (!equipments.length) return null;
  return (
    <Typography $variant="label">
      {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_selected_extras' })}
      <Extras $variant="body-sm">
        {equipments.map(({ text }) => text).join(', ')}
      </Extras>
    </Typography>
  )
}

function CarAvailableEquipments ({equipments}: {equipments: {id: string; text: string}[]}) {
  const intl = useIntl();
  if (!equipments.length) return null;

  return (
    <Extras $variant="body-xs">
      {intl.formatMessage(
        { id: 'checkout_steps_ancillaries_car_rental_optional_extras' },
        { extras: equipments.map(({ text }) => text).join(', ') }
      )}
    </Extras>
  )
}

const CarImage = styled.img`
  width: 180px;
  height: auto;
`;
const CarContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  @media ${tk('mq_desktop_sm')} {
    flex-direction: row;
    width: 100%;
  }
`;
const CarInformation = styled(SpaceBetween)`
  flex: 1;
`;
const CarClass = styled(Typography)`
  color: ${tk('color_palette_primary_500')};
`;
const CarFeatures = styled(BulletList.Root)`
  display: flex;
  flex-wrap: wrap;
  column-gap: ${tk('spacing_xs')};
`;
const CarRentalUsp = styled(BulletList.Item)``;
const CarPriceView = styled(SpaceBetween)`
  align-self: flex-end;
  margin-top: ${tk('spacing_sm')};
  @media ${tk('mq_desktop_sm')} {
    align-self: center;
    margin-top: 0;
  }
`;
const Price = styled(Typography)`
  display: flex;
  align-items: center;
`;
const PriceDetail = styled(Typography)`
  color: ${tk('color_semantic_text_strong')};
  display: flex;
  align-items: center;
  white-space: nowrap;
`;
const AvailableOnRequestBadge = styled(ListItem.Badge)`
  width: fit-content;
`;
const Extras = styled(Typography)`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
`

